/** Copyright 2025 SAP SE or an SAP affiliate company. All rights reserved. */
import useSessionManager from '@/hooks/useSessionManager';
import { useTranslations } from '@/hooks/useTranslations';
import { LocalStorageKeys } from '@/utils/axiosInstance';
import * as storageUtils from '@/utils/storageUtils';
import { createStyles } from '@/utils/styles';
import { Button, Dialog, FlexBox, IllustratedMessage } from '@ui5/webcomponents-react';
import { ThemingParameters } from '@ui5/webcomponents-react-base';
import '@ui5/webcomponents-fiori/dist/illustrations/tnt/SessionExpiring.js';
import '@ui5/webcomponents-fiori/dist/illustrations/tnt/SessionExpired.js';
import { useEffect, useRef } from 'react';

const SessionDialog: React.FC = () => {
    const authToken = storageUtils.getItem(LocalStorageKeys.ACCESS_TOKEN);
    const refreshToken = storageUtils.getItem(LocalStorageKeys.REFRESH_TOKEN);
    const { showWarning, remainingTime, extendSession, closeSession, loginSession, signoutSession } = useSessionManager(authToken, refreshToken);
    const { t } = useTranslations();
    const ONE_MINUTE = 60 * 1000;
    const minutesLeft = Math.ceil(remainingTime / ONE_MINUTE);
    const originalTitleRef = useRef<string>('');

    useEffect(() => {
        if (!authToken) return;

        // Capture the original title when the warning first appears
        if (showWarning && originalTitleRef.current === '') {
            originalTitleRef.current = document.title;
        }

        if (showWarning) {
            document.title = t('SESSION_EXPIRED_WARNING_MESSAGE');
        } else if (originalTitleRef.current !== '') {
            // Restore the original title when dialog closes mid-time or session resets
            document.title = originalTitleRef.current;
            originalTitleRef.current = '';
        }

        // Cleanup: Restore original title on unmount if still set
        return () => {
            if (originalTitleRef.current !== '') {
                document.title = originalTitleRef.current;
            }
        };
    }, [showWarning, authToken]);

    return (
        <>
            {authToken && remainingTime > 0 ? (
                <Dialog
                    open={showWarning}
                    onClose={closeSession}
                    headerText={t('SESSION_INFORMATION')}
                    style={styles.container}
                    footer={
                        <FlexBox alignItems="Center" style={styles.buttonsWrapper} justifyContent="End">
                            <Button design="Emphasized" onClick={extendSession}>
                                {t('KEEP_WORKING')}
                            </Button>
                            <Button onClick={signoutSession}>{t('SIGNOUT_BUTTON')}</Button>
                        </FlexBox>
                    }
                >
                    <IllustratedMessage
                        name="TntSessionExpiring"
                        titleText={t('SESSION_EXPIRED_WARNING_MESSAGE', minutesLeft.toString(), minutesLeft !== 1 ? 's' : '')}
                    ></IllustratedMessage>
                </Dialog>
            ) : (
                <Dialog
                    open={showWarning}
                    onClose={closeSession}
                    headerText={t('SESSION_EXPIRED')}
                    style={styles.container}
                    footer={
                        <FlexBox alignItems="Center" style={styles.buttonsWrapper} justifyContent="End">
                            <Button design="Emphasized" onClick={loginSession}>
                                {t('LOGIN_BUTTON')}
                            </Button>
                            <Button onClick={closeSession}>{t('CLOSE_BUTTON')}</Button>
                        </FlexBox>
                    }
                >
                    <IllustratedMessage name="TntSessionExpired" titleText={t('SESSION_EXPIRED_MESSAGE')}></IllustratedMessage>
                </Dialog>
            )}
        </>
    );
};

const styles = createStyles({
    container: {
        maxWidth: '400px',
    },
    buttonsWrapper: {
        width: '100%',
        margin: ThemingParameters.sapShell_Space_S,
        gap: ThemingParameters.sapShell_Space_S,
    },

    text: {
        margin: ThemingParameters.sapShell_Space_S,
    },
});

export default SessionDialog;
